import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authorizedFetch } from '@/lib/api'

/** `type` is `<app_label>.<model>`, e.g. `opportunities.opportunity`. */
export type DiscourseSubjectRef = {
  type: string
  id: string
}

export type DiscourseSessionDetail = {
  id: string
  client_config: string
  kind: string
  modality: string
  status: string
  title: string
  transcript: string
  support_thread_id?: string | null
  subject?: DiscourseSubjectRef | null
  related?: DiscourseSubjectRef[]
  profile_id?: string | null
  metadata?: Record<string, unknown>
  participants: Array<{
    id: string
    party_type: string
    role_label: string
    display_name: string
  }>
  turns: Array<{
    id: string
    sequence: number
    role: string
    text: string
    turn_id: string
    source: string
  }>
}

export type ClassifierRun = {
  id: string
  discourse_session_id: string
  profile_id: string
  generation: number
  status: string
  output: Record<string, unknown>
  error: string
  updated_at: string
}

export type CreateDiscourseSessionBody = {
  kind: string
  modality?: string
  title?: string
  subject?: DiscourseSubjectRef | null
  related?: DiscourseSubjectRef[]
  profile_id?: string | null
  rubric_revision_id?: string | null
  classifier_profile_id?: string | null
  demo_mode?: boolean
  demo_scenario_hint?: string | null
  metadata?: Record<string, unknown>
}

export function useDiscourseSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['discourse-session', sessionId],
    enabled: Boolean(sessionId),
    queryFn: async (): Promise<DiscourseSessionDetail> => {
      const res = await authorizedFetch(`/api/discourse/sessions/${sessionId}`)
      if (!res.ok) throw new Error('Failed to load discourse session')
      return res.json()
    },
  })
}

export function useClassifierLatest(
  sessionId: string | undefined,
  profileId: string | undefined,
) {
  return useQuery({
    queryKey: ['classifier-latest', sessionId, profileId],
    enabled: Boolean(sessionId && profileId),
    queryFn: async (): Promise<ClassifierRun> => {
      const q = profileId ? `?profile_id=${encodeURIComponent(profileId)}` : ''
      const res = await authorizedFetch(
        `/api/classifiers/sessions/${sessionId}/latest${q}`,
      )
      if (res.status === 404) {
        return {
          id: '',
          discourse_session_id: sessionId || '',
          profile_id: profileId || '',
          generation: 0,
          status: 'pending',
          output: {},
          error: '',
          updated_at: '',
        }
      }
      if (!res.ok) throw new Error('Failed to load classifier run')
      return res.json()
    },
  })
}

export function useCreateDiscourseSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateDiscourseSessionBody) => {
      const res = await authorizedFetch('/api/discourse/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Failed to create session')
      return res.json() as Promise<{
        id: string
        join_token?: string | null
        support_thread_id?: string | null
        modality?: string
        status?: string
      }>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['discourse-session'] })
    },
  })
}

export function useAppendDiscourseTurn(sessionId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      sequence?: number
      role: string
      text: string
      turn_id?: string
      source?: string
    }) => {
      const res = await authorizedFetch(`/api/discourse/sessions/${sessionId}/turns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Failed to append turn')
      return res.json()
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['discourse-session', sessionId] })
      void qc.invalidateQueries({ queryKey: ['classifier-latest', sessionId] })
    },
  })
}

export function useSimulateDiscourseTurn(sessionId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      speaker: 'protagonist' | 'counterpart'
      signal?: AbortSignal
    }) => {
      const { signal, ...payload } = body
      const res = await authorizedFetch(
        `/api/discourse/sessions/${sessionId}/simulate-turn`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal,
        },
      )
      if (!res.ok) throw new Error('Failed to simulate turn')
      return res.json() as Promise<{
        id: string
        sequence: number
        role: string
        text: string
        source: string
      }>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['discourse-session', sessionId] })
      void qc.invalidateQueries({ queryKey: ['classifier-latest', sessionId] })
    },
  })
}

export function useDailyOwnerToken(sessionId: string | undefined) {
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(`/api/discourse/sessions/${sessionId}/daily-token`, {
        method: 'POST',
      })
      if (!res.ok) throw new Error('Could not join the video room')
      return res.json() as Promise<{ room_url: string; token: string; session_id: string }>
    },
  })
}

export function useRotateInterviewJoinLink(sessionId: string | undefined) {
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(`/api/discourse/sessions/${sessionId}/join-link`, {
        method: 'POST',
      })
      if (!res.ok) throw new Error('Could not create a guest link')
      return res.json() as Promise<{ join_token: string; session_id: string }>
    },
  })
}

export function useCompleteDiscourseSession(sessionId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(`/api/discourse/sessions/${sessionId}/complete`, {
        method: 'POST',
      })
      if (!res.ok) throw new Error('Could not end the interview')
      return res.json()
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['discourse-session', sessionId] })
    },
  })
}
